<!-- `overflow-x-clip` et non `overflow-x-hidden` : « hidden » sur un seul axe
     force l'autre en `auto` et fabrique un conteneur de défilement imbriqué,
     ce qui casserait le scroll de la page. `clip` se contente de couper. -->
<div class="overflow-x-clip" @pageFromRight>
<vw-page-header [title]="'weather.detail.title' | translate" (back)="goBack()"></vw-page-header>

<div class="px-4 pb-28">

  @if (isLoading) {
    <vw-skeleton-loader [width]="'100%'" [height]="'140px'" [borderRadius]="'0.75rem'"></vw-skeleton-loader>
  } @else if (failed) {
    <!-- Une panne n'est pas une adresse sans météo : « Réessayer » recharge. -->
    <vw-error-state [retrying]="isLoading" (retry)="load()"></vw-error-state>
  } @else if (!days.length) {
    <vw-empty-state willy="dontknow" titleKey="weather.detail.empty-title" messageKey="weather.detail.unavailable"
      actionKey="weather.detail.check-address" (action)="checkAddress()"></vw-empty-state>
  } @else {

  <!-- Le verdict d'abord : l'écran donnait la matière et laissait conclure,
       alors que la conclusion est justement ce que l'app sait faire. -->
  @if (verdict; as v) {
    <div
      class="flex items-start gap-3 rounded-2xl px-4 py-3.5"
      [style.background-color]="style(v.condition).bg"
    >
      <span class="text-2xl leading-none">{{ style(v.condition).emoji }}</span>
      <p class="text-[15px] font-medium leading-snug" [style.color]="style(v.condition).ink">
        {{ v.key | translate: v.params }}
      </p>
    </div>
  }

  <!-- Trois chiffres, trois questions : depuis quand est-ce sec, quand
       pleut-il, et jusqu'où monte-t-il. La hauteur réservée sur les libellés
       aligne les valeurs — sans elle, celui qui tient sur deux lignes fait
       descendre son chiffre. -->
  <div class="mt-2 grid grid-cols-3 gap-2">
    <div class="rounded-2xl bg-white px-3 py-3 shadow-sm">
      <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-vw-muted">
        {{ 'weather.detail.last-rain' | translate }}
      </p>
      <!-- Jours finis seulement : la pluie du jour ne remet pas « 0 j » sous « pluie aujourd'hui » (B2). -->
      <p class="mt-1 text-body-s font-semibold text-primary-dark" data-test="last-rain">
        @if ((outlook?.dryStreak ?? 0) === 0) {
          {{ 'weather.detail.last-rain-yesterday' | translate }}
        } @else {
          {{ 'weather.detail.last-rain-days' | vwPlural: (outlook?.dryStreak ?? 0) + 1 }}
        }
      </p>
    </div>

    <!-- La question posée à cette tuile est « quand ? », et la réponse est une
         échéance, pas une quantité. Un « 0.6 mm » en gros chiffre donnait une
         précision trompeuse pour une pluie qui n'arrose rien ; le cumul reste
         dessous, en second rang. -->
    <div class="rounded-2xl bg-white px-3 py-3 shadow-sm">
      <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-vw-muted">
        {{ 'weather.detail.next-rain' | translate }}
      </p>
      <p class="mt-1 text-body-s font-semibold text-primary-dark" data-test="next-rain">
        @if (nextRainInDays === null) {
          {{ 'weather.detail.rain-none' | translate }}
        } @else if (nextRainInDays === 0) {
          {{ 'weather.detail.rain-today-mm' | translate: { mm: num(todayRainMm) } }}
        } @else if (nextRainInDays === 1) {
          {{ 'weather.detail.rain-tomorrow' | translate }}
        } @else {
          {{ 'weather.detail.rain-in-days' | vwPlural: nextRainInDays }}
        }
      </p>
      <p class="mt-0.5 text-[10px] leading-tight text-vw-muted">
        {{ 'weather.detail.rain-total' | translate: { mm: num(outlook?.upcomingRainMm ?? 0) } }}
      </p>
    </div>

    <!-- Le pic à venir : c'est lui qu'on prépare, pas la moyenne. D'octobre à
         avril, c'est la nuit la plus froide : par nuit claire, le sol gèle
         dès 3 °C sous abri, le serveur le signale sur la journée. -->
    @if (coldSeason) {
      <div class="rounded-2xl bg-white px-3 py-3 shadow-sm" data-test="coldest-night">
        <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-vw-muted">
          {{ 'weather.detail.coldest-night' | translate }}
        </p>
        @if (coldestNight; as night) {
          <p class="mt-1 text-xl font-semibold text-primary-dark">
            {{ num(night.tempMin) }}<span class="text-xs font-normal text-vw-muted">°</span>
          </p>
          <p class="text-[10px] leading-tight text-vw-muted">
            {{ night.isToday ? ('weather.detail.today' | translate) : night.weekday + ' ' + night.dayNumber }}
          </p>
          @if (night.groundFrost) {
            <p class="mt-1 inline-block rounded-full bg-[#FDF0D5] px-2 py-0.5 text-[10px] font-medium leading-tight text-[#8A5A0B]" data-test="ground-frost">
              {{ 'weather.detail.ground-frost' | translate }}
            </p>
          }
        } @else {
          <p class="mt-1 text-xl font-semibold text-vw-muted">—</p>
        }
      </div>
    } @else {
      <div class="rounded-2xl bg-white px-3 py-3 shadow-sm" data-test="peak">
        <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-vw-muted">
          {{ 'weather.detail.peak' | translate }}
        </p>
        @if (peakDay; as peak) {
          <p class="mt-1 text-xl font-semibold text-primary-dark">
            {{ num(peak.tempMax) }}<span class="text-xs font-normal text-vw-muted">°</span>
          </p>
          <p class="text-[10px] leading-tight text-vw-muted">
            {{ peak.isToday ? ('weather.detail.today' | translate) : peak.weekday + ' ' + peak.dayNumber }}
          </p>
        } @else {
          <p class="mt-1 text-xl font-semibold text-vw-muted">—</p>
        }
      </div>
    }
  </div>

  <!-- La fenêtre, jour par jour -->
  <h3 class="mt-6 text-lg font-semibold text-primary-dark">
    {{ 'weather.detail.window' | translate }}
  </h3>
  <!-- Une barre sans légende n'est pas une donnée, c'est une décoration :
       la précédente montrait la pluie au prorata du jour le plus arrosé, ce
       qui la rendait illisible une semaine sèche. -->
  <p class="pb-2 text-xs leading-snug text-vw-muted">
    {{ 'weather.detail.temp-legend' | translate }}
  </p>

  <div class="flex flex-col gap-2">
    @for (day of days; track day.date) {
      <div
        class="rounded-2xl border-2 px-3 py-2"
        [style.background-color]="rowBackground(day.condition)"
        [style.border-color]="day.isToday ? style(day.condition).ink : 'transparent'"
        [class.opacity-70]="day.isPast"
      >
        <div class="flex items-center gap-3">
          <!-- Date. Le jour même porte sa pastille pleine : le mot
               « aujourd'hui » en plus du cadre volait la place du libellé, qui
               se retrouvait tronqué en « Forte chal… ». -->
          <div class="w-10 shrink-0 text-center">
            <p class="text-[11px] uppercase leading-tight text-vw-muted">{{ day.weekday }}</p>
            @if (day.isToday) {
              <p
                class="mx-auto mt-0.5 h-6 w-6 rounded-full text-base font-semibold leading-6 text-white"
                [style.background-color]="style(day.condition).ink"
                [attr.aria-label]="'weather.detail.today' | translate"
              >
                {{ day.dayNumber }}
              </p>
            } @else {
              <p class="text-base font-semibold leading-tight" [style.color]="style(day.condition).ink">
                {{ day.dayNumber }}
              </p>
            }
          </div>

          <span class="shrink-0 text-xl">{{ style(day.condition).emoji }}</span>

          <p class="min-w-0 flex-1 truncate text-sm font-medium" [style.color]="style(day.condition).ink">
            {{ 'weather.detail.conditions.' + day.condition | translate }}
          </p>

          <!-- Pluie et vent : signalés quand ils changent quelque chose. Sous
               le seuil, rien — une averse de 0,36 mm ne mouille pas le sol, et
               l'annoncer ferait renoncer à un arrosage nécessaire. -->
          @if (countsAsRain(day)) {
            <span class="shrink-0 text-[11px] font-medium text-[#1F4E85]">
              💧 {{ num(day.precipitation) }} mm
            </span>
          }
          @if (day.windSpeed && day.windSpeed >= windNotable) {
            <span class="shrink-0 text-[11px] text-[#3E5250]">💨 {{ day.windSpeed }}</span>
          }
        </div>

        <!-- La barre thermique, sur une échelle COMMUNE à la fenêtre : c'est
             ce qui fait voir la chaleur monter puis retomber. -->
        <div class="mt-1 flex items-center gap-2">
          <span class="w-10 shrink-0 text-right text-[11px] text-vw-muted">
            {{ day.tempMin === null ? '—' : num(day.tempMin) }}°
          </span>
          <div class="relative h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
            @if (day.tempMin !== null && day.tempMax !== null) {
              <div
                class="absolute inset-y-0 rounded-full"
                [style.left.%]="bandLeft(day)"
                [style.width.%]="bandWidth(day)"
                [style.background-color]="style(day.condition).bar"
              ></div>
            }
          </div>
          <span class="w-10 shrink-0 text-[11px] font-semibold" [style.color]="style(day.condition).ink">
            {{ day.tempMax === null ? '—' : num(day.tempMax) }}°
          </span>
        </div>
      </div>
    }
  </div>

  <p class="mt-4 text-xs leading-relaxed text-vw-muted">
    {{ 'weather.detail.footnote' | translate }}
  </p>
  }
</div>
</div>
